import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { DESIGNS, DESIGN_CATEGORIES, DESIGN_STYLES } from "@/data/urban";
import { GALLERY as TID } from "@/constants/testIds";

export const Gallery = () => {
  const [cat, setCat] = useState("All");
  const [style, setStyle] = useState("All Styles");
  const [idx, setIdx] = useState(null);

  const filtered = useMemo(() =>
    DESIGNS.filter((d) => (cat === "All" || d.cat === cat) && (style === "All Styles" || d.style === style)),
    [cat, style]
  );

  return (
    <section id="designs" className="py-20 md:py-28 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        <Reveal><div className="overline text-ink-soft">Designs & Inspiration</div></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-ink leading-tight">Design ideas for your home.</h2>
        </Reveal>

        <div className="mt-8 flex flex-wrap gap-2">
          {DESIGN_CATEGORIES.map((c) => (
            <button key={c} type="button" data-testid={TID.filter(c.toLowerCase().replace(/\s+/g, "-"))}
              onClick={() => setCat(c)}
              className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider border transition-colors ${cat === c ? "bg-black text-white border-black" : "bg-white text-ink border-hairline hover:border-ink"}`}>{c}</button>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {DESIGN_STYLES.map((s) => (
            <button key={s} type="button" onClick={() => setStyle(s)}
              className={`px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-wider border transition-colors ${style === s ? "bg-ink text-white border-ink" : "bg-canvas text-ink-soft border-hairline hover:border-ink"}`}>{s}</button>
          ))}
        </div>

        <motion.div layout className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <AnimatePresence mode="popLayout">
            {filtered.map((d, i) => (
              <motion.button layout key={d.title} type="button" data-testid={TID.item(i)} onClick={() => setIdx(i)}
                initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                className="group relative rounded-xl overflow-hidden bg-canvas border border-hairline">
                <div className="aspect-[4/5] overflow-hidden"><img src={d.img} alt={d.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-0 left-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity text-white">
                  <div className="text-[10px] uppercase tracking-widest text-white/70">{d.cat} · {d.style}</div>
                  <div className="text-sm font-bold">{d.title}</div>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {idx !== null && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setIdx(null)}>
            <button type="button" data-testid={TID.close} onClick={() => setIdx(null)} className="absolute top-5 right-5 h-11 w-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"><X className="h-5 w-5" /></button>
            <motion.img key={idx} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} src={filtered[idx].img} alt={filtered[idx].title} className="max-h-[85vh] max-w-[85vw] object-contain rounded-2xl" onClick={(e) => e.stopPropagation()} />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
